import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  Store, Sparkles, TrendingUp, Package, Globe, Zap,
  CheckCircle, ArrowRight, Star, ShoppingBag, BarChart3,
  Users, Award, Shield, Crown, Layers, Palette,
  Check, UserPlus, Upload, Rocket, Play, X, Eye,
  MessageCircle, CreditCard, AlertCircle, Smartphone,
  Instagram, Layout, ImageIcon, Monitor, Mail, Phone,
  Calendar, ClipboardList, DollarSign, TrendingDown,
  MousePointer, Type, Image as ImageIconLucide, PenTool,
  Send, PartyPopper, Laptop, Coffee
} from 'lucide-react';
import UniversalNavbar from '../components/UniversalNavbar';
import VFooter from '../components/VFooter';
import COLORS from '../utils/colors';

const LandingPage = () => {
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Animated counter component
  const AnimatedCounter = ({ end, suffix = '', duration = 2000 }) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, amount: 0.5 });

    useEffect(() => {
      if (isInView) {
        let start = 0;
        const increment = end / (duration / 16);
        const timer = setInterval(() => {
          start += increment;
          if (start >= end) {
            setCount(end);
            clearInterval(timer);
          } else {
            setCount(Math.floor(start));
          }
        }, 16);
        return () => clearInterval(timer);
      }
    }, [isInView, end, duration]);

    return <span ref={ref}>{count}{suffix}</span>;
  };

  // Optimized scroll reveal
  const FadeInWhenVisible = ({ children, delay = 0 }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-80px" });

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 60 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
        transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>
    );
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <div className="min-h-screen bg-white font-fell">
      <UniversalNavbar />

      {/* 1️⃣ HERO SECTION */}
      <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-5xl mx-auto">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-gray-200 rounded-full text-sm font-semibold text-gray-900 mb-6 shadow-sm"
            >
              <Sparkles className="w-4 h-4" style={{ color: COLORS.AUTUMN }} />
              <span>Built Exclusively for Clothing Brands</span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 sm:mb-8 leading-[1.1] tracking-tight"
            >
              Create Your Dream Website
              <br />
              in 5 minutes
            </motion.h1>

            {/* Subheadline */}
            {/* <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl sm:text-2xl md:text-3xl text-gray-600 mb-4 leading-relaxed"
            >
              Get a professional website for your clothing brand under{' '}
              <span className="font-bold px-2 py-1 rounded bg-orange-50" style={{ color: COLORS.AUTUMN }}>
                5 minutes
              </span>
            </motion.p> */}

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-lg sm:text-xl text-gray-600 mb-10 sm:mb-12"
            >
              {/* Not another marketplace. <span className="font-semibold text-gray-900">Your own branded website.</span>
              <br /> */}
              Simple to setup. No technical skills needed.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10"
            >
              <Link to="/Register">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-10 py-5 rounded-xl font-bold text-white text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
                  style={{ backgroundColor: COLORS.AUTUMN }}
                >
                  <span className="flex items-center gap-2">
                    Create My Website
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </motion.button>
              </Link>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowVideoModal(true)}
                className="px-10 py-5 rounded-xl font-bold text-gray-700 text-lg border-2 border-gray-300 hover:border-gray-400 transition-all duration-300 bg-white"
              >
                <span className="flex items-center gap-2">
                  <Play className="w-5 h-5" />
                  Watch 2-Min Demo
                </span>
              </motion.button>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="flex flex-wrap justify-center items-center gap-6 text-sm text-gray-600 mb-8"
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium">100% Free to Start</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium">No Credit Card Required</span>
              </div>
              {/* <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium">Live in 5 Minutes</span>
              </div> */}
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="pt-8 border-t-2 border-gray-200"
            >
              <div className="text-xs text-gray-500 mb-4 font-medium uppercase tracking-wide">Powered By Industry Leaders</div>
              <div className="flex flex-wrap justify-center items-center gap-6">
                <div className="flex items-center gap-2 px-5 py-3 bg-white border-2 border-gray-200 rounded-xl">
                  <img src="https://razorpay.com/assets/razorpay-glyph.svg" alt="Razorpay" className="h-6" />
                  <div className="text-left">
                    <div className="text-xs text-gray-900 font-bold">Razorpay</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-5 py-3 bg-white border-2 border-gray-200 rounded-xl">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg" alt="AWS" className="h-6" />
                  <div className="text-left">
                    <div className="text-xs text-gray-900 font-bold">AWS Cloud</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2️⃣ TRUST / SOCIAL PROOF SECTION */}
      <FadeInWhenVisible>
        <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white border-y-2 border-gray-200">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-gray-600 text-lg mb-8">Used by Clothing Brands and boutique owners across India</p>
            </div>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-2 lg:grid-cols-4 gap-8"
            >
              <motion.div variants={itemVariants} className="text-center">
                <div className="w-12 h-12 rounded-xl bg-white border-2 border-gray-200 flex items-center justify-center mx-auto mb-3">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-4xl sm:text-5xl font-bold text-blue-600 mb-2">
                  <AnimatedCounter end={20} suffix="+" />
                </div>
                <div className="text-gray-600 text-sm font-medium">Website theams</div>
              </motion.div>
              <motion.div variants={itemVariants} className="text-center">
                <div className="w-12 h-12 rounded-xl bg-white border-2 border-gray-200 flex items-center justify-center mx-auto mb-3">
                  <ShoppingBag className="w-6 h-6 text-green-600" />
                </div>
                <div className="text-4xl sm:text-5xl font-bold text-green-600 mb-2">
                  <AnimatedCounter end={100} suffix="+" />
                </div>
                <div className="text-gray-600 text-sm font-medium">Happy Customers</div>
              </motion.div>
              <motion.div variants={itemVariants} className="text-center">
                <div className="w-12 h-12 rounded-xl bg-white border-2 border-gray-200 flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
                <div className="text-4xl sm:text-5xl font-bold text-purple-600 mb-2">
                  <AnimatedCounter end={100} suffix="%" />
                </div>
                <div className="text-gray-600 text-sm font-medium">Uptime Guarantee</div>
              </motion.div>
              <motion.div variants={itemVariants} className="text-center">
                <div className="w-12 h-12 rounded-xl bg-white border-2 border-gray-200 flex items-center justify-center mx-auto mb-3">
                  <Star className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="text-4xl sm:text-5xl font-bold text-yellow-600 mb-2">
                  <AnimatedCounter end={4.9} suffix="/5" />
                </div>
                <div className="text-gray-600 text-sm font-medium">Platform Rating</div>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </FadeInWhenVisible>

      {/* What You Can Do With Vasstr Section */}
      <FadeInWhenVisible>
        <section className="py-20 sm:py-32 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                What you can do with Vasstr
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Everything you need to run your fashion brand online — in one place
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
              {[
                {
                  icon: Crown,
                  title: "Build your brand online",
                  description: "Create a professional website that represents your designs, story, and collections.",
                  color: "blue"
                },
                {
                  icon: ShoppingBag,
                  title: "Take orders professionally",
                  description: "Let customers explore your work, place orders, and request custom designs clearly.",
                  color: "green"
                },
                {
                  icon: BarChart3,
                  title: "Manage everything in one place",
                  description: "Orders, payments, and updates — organised and easy to handle.",
                  color: "purple"
                }
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white border-2 border-gray-200 rounded-2xl p-8"
                >
                  <div className={`w-14 h-14 rounded-xl bg-${item.color}-50 border-2 border-${item.color}-200 flex items-center justify-center mb-6`}>
                    <item.icon className={`w-7 h-7 text-${item.color}-600`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <p className="text-xl text-gray-600">
                Built for designers, boutiques, tailors, and clothing brands.
              </p>
            </div>
          </div>
        </section>
      </FadeInWhenVisible>

      {/* 🎨 NEW: ANIMATED PROCESS VISUAL SECTION */}
      <FadeInWhenVisible>
        <section className="py-20 sm:py-32 px-4 sm:px-6 lg:px-8 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-20">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="inline-block px-4 py-2 bg-white border-2 border-gray-200 rounded-full text-sm font-semibold mb-6"
                style={{ color: COLORS.AUTUMN }}
              >
                Watch It Come To Life
              </motion.div>
              <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                From Idea to Live Website
                <br />
                <span className="text-gray-600">in Just 5 Minutes</span>
              </h2>
            </div>

            {/* Step 1: Sign Up */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="mb-32 relative"
            >
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div className="order-2 lg:order-1">
                  <div className="relative">
                    {/* Floating Step Badge */}
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      whileInView={{ scale: 1, rotate: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.2, type: "spring" }}
                      className="absolute -top-6 -left-6 w-20 h-20 rounded-full flex items-center justify-center text-3xl font-black text-white shadow-2xl z-10"
                      style={{ backgroundColor: COLORS.AUTUMN }}
                    >
                      1
                    </motion.div>

                    {/* Animated Screen Mockup */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9, rotateY: -15 }}
                      whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.3 }}
                      className="bg-white border-4 border-gray-200 rounded-3xl shadow-2xl p-8 relative overflow-hidden"
                      style={{ transformStyle: "preserve-3d" }}
                    >
                      {/* Browser Chrome */}
                      <div className="flex items-center gap-2 mb-6 pb-4 border-b-2 border-gray-200">
                        <div className="flex gap-1.5">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        </div>
                        <div className="flex-1 bg-white border-2 border-gray-200 rounded-lg px-3 py-1 text-xs text-gray-500">
                          vasstr.com/register
                        </div>
                      </div>

                      {/* Animated Form */}
                      <div className="space-y-4">
                        <motion.div
                          initial={{ width: 0, opacity: 0 }}
                          whileInView={{ width: "100%", opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.6 }}
                        >
                          <div className="flex items-center gap-3 bg-white border-2 border-gray-200 rounded-xl p-4">
                            <Type className="w-5 h-5 text-gray-400" />
                            <motion.div
                              initial={{ opacity: 0 }}
                              whileInView={{ opacity: 1 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.3, delay: 1 }}
                              className="text-gray-600 font-medium"
                            >
                              fashion_designer_priya
                            </motion.div>
                          </div>
                        </motion.div>

                        <motion.div
                          initial={{ width: 0, opacity: 0 }}
                          whileInView={{ width: "100%", opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.8 }}
                        >
                          <div className="flex items-center gap-3 bg-white border-2 border-gray-200 rounded-xl p-4">
                            <Mail className="w-5 h-5 text-gray-400" />
                            <motion.div
                              initial={{ opacity: 0 }}
                              whileInView={{ opacity: 1 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.3, delay: 1.2 }}
                              className="text-gray-600 font-medium"
                            >
                              priya@example.com
                            </motion.div>
                          </div>
                        </motion.div>

                        <motion.button
                          initial={{ scale: 0, opacity: 0 }}
                          whileInView={{ scale: 1, opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 1.4, type: "spring", stiffness: 200 }}
                          whileHover={{ scale: 1.05 }}
                          className="w-full py-4 rounded-xl font-bold text-white text-lg shadow-xl flex items-center justify-center gap-2"
                          style={{ backgroundColor: COLORS.AUTUMN }}
                        >
                          Create My Website
                          <motion.div
                            animate={{ x: [0, 5, 0] }}
                            transition={{ duration: 1, repeat: Infinity }}
                          >
                            <ArrowRight className="w-5 h-5" />
                          </motion.div>
                        </motion.button>
                      </div>

                      {/* Floating Success Checkmark */}
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 1.8, type: "spring" }}
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full bg-green-500 flex items-center justify-center shadow-2xl"
                      >
                        <CheckCircle className="w-12 h-12 text-white" strokeWidth={3} />
                      </motion.div>

                      {/* Animated Confetti Particles */}
                      {[...Array(8)].map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ scale: 0, x: 0, y: 0, opacity: 0 }}
                          whileInView={{
                            scale: [0, 1, 0],
                            x: [(i % 2 === 0 ? -1 : 1) * (50 + i * 20)],
                            y: [-50 - i * 10],
                            opacity: [0, 1, 0]
                          }}
                          viewport={{ once: true }}
                          transition={{ duration: 1, delay: 1.9 + i * 0.1 }}
                          className="absolute top-1/2 left-1/2 w-3 h-3 rounded-full"
                          style={{
                            backgroundColor: [COLORS.AUTUMN, '#10b981', '#3b82f6', '#f59e0b'][i % 4]
                          }}
                        />
                      ))}
                    </motion.div>

                    {/* Floating Person Illustration */}
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.4 }}
                      className="absolute -bottom-8 -left-12 hidden lg:block"
                    >
                      <div className="relative">
                        <motion.div
                          animate={{ y: [0, -10, 0] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                          className="w-32 h-32 rounded-full flex items-center justify-center text-6xl bg-white border-4 border-gray-200 shadow-xl"
                        >
                          👤
                        </motion.div>
                        <motion.div
                          initial={{ scale: 0 }}
                          whileInView={{ scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: 1.5 }}
                          className="absolute -top-2 -right-2 w-10 h-10 rounded-full flex items-center justify-center text-2xl bg-green-500 border-4 border-white shadow-lg"
                        >
                          ✓
                        </motion.div>
                      </div>
                    </motion.div>
                  </div>
                </div>

                <div className="order-1 lg:order-2">
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-16 h-16 rounded-2xl bg-blue-50 border-2 border-blue-200 flex items-center justify-center">
                        <UserPlus className="w-8 h-8 text-blue-600" />
                      </div>
                      <div className="h-1 w-20 rounded-full" style={{ backgroundColor: COLORS.AUTUMN }}></div>
                    </div>
                    <h3 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                      Sign Up in Seconds
                    </h3>
                    <p className="text-xl text-gray-600 leading-relaxed mb-8">
                      Just enter your brand name and email. No credit card, no complicated forms.
                      Your website URL is created instantly at <span className="font-bold" style={{ color: COLORS.AUTUMN }}>yourname.vasstr.com</span>
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-gray-700">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                        <span className="text-lg">Takes less than 30 seconds</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-700">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                        <span className="text-lg">No payment required</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-700">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                        <span className="text-lg">Instant website creation</span>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Step 2: Upload Designs */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="mb-32 relative"
            >
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div className="order-2">
                  <div className="relative">
                    {/* Floating Step Badge */}
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      whileInView={{ scale: 1, rotate: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.2, type: "spring" }}
                      className="absolute -top-6 -right-6 w-20 h-20 rounded-full flex items-center justify-center text-3xl font-black text-white shadow-2xl z-10"
                      style={{ backgroundColor: COLORS.AUTUMN }}
                    >
                      2
                    </motion.div>

                    {/* Animated Upload Interface */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9, rotateY: 15 }}
                      whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.3 }}
                      className="bg-white border-4 border-gray-200 rounded-3xl shadow-2xl p-8 relative overflow-hidden"
                    >
                      {/* Dashboard Header */}
                      <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white text-sm" style={{ backgroundColor: COLORS.AUTUMN }}>
                            PD
                          </div>
                          <div>
                            <div className="text-sm font-bold text-gray-900">Priya's Dashboard</div>
                            <div className="text-xs text-gray-500">fashion_designer_priya.vasstr.com</div>
                          </div>
                        </div>
                      </div>

                      {/* Upload Area */}
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.6 }}
                        className="border-4 border-dashed border-gray-300 rounded-2xl p-8 text-center mb-6 bg-white"
                      >
                        <motion.div
                          animate={{ y: [0, -10, 0] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                          className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-4"
                          style={{ backgroundColor: `${COLORS.AUTUMN}15` }}
                        >
                          <Upload className="w-10 h-10" style={{ color: COLORS.AUTUMN }} />
                        </motion.div>
                        <p className="text-gray-700 font-semibold mb-2">Drag photos here or click to upload</p>
                        <p className="text-sm text-gray-500">Up to 6 photos per design</p>
                      </motion.div>

                      {/* Uploaded Photos Grid */}
                      <div className="grid grid-cols-3 gap-3">
                        {[0, 1, 2].map((index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0, rotate: -180 }}
                            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                            viewport={{ once: true }}
                            transition={{
                              duration: 0.5,
                              delay: 1 + index * 0.15,
                              type: "spring",
                              stiffness: 200
                            }}
                            whileHover={{ scale: 1.05 }}
                            className="aspect-square bg-gradient-to-br from-orange-100 to-pink-100 rounded-xl relative overflow-hidden border-2 border-gray-200 cursor-pointer group"
                          >
                            <div className="absolute inset-0 flex items-center justify-center text-4xl">
                              👗
                            </div>
                            <motion.div
                              initial={{ opacity: 0 }}
                              whileInView={{ opacity: 1 }}
                              viewport={{ once: true }}
                              transition={{ delay: 1.5 + index * 0.15 }}
                              className="absolute top-2 right-2 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Check className="w-4 h-4 text-white" strokeWidth={3} />
                            </motion.div>
                          </motion.div>
                        ))}
                      </div>

                      {/* Details Form */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 1.6 }}
                        className="mt-6 space-y-3"
                      >
                        <div className="flex items-center gap-3 bg-white border-2 border-gray-200 rounded-xl p-3 text-sm">
                          <PenTool className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">Elegant Evening Dress</span>
                        </div>
                        <div className="flex items-center gap-3 bg-white border-2 border-gray-200 rounded-xl p-3 text-sm">
                          <DollarSign className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">₹2,999</span>
                        </div>
                      </motion.div>

                      {/* Publish Button */}
                      <motion.button
                        initial={{ scale: 0, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 1.9, type: "spring" }}
                        whileHover={{ scale: 1.05 }}
                        className="w-full mt-6 py-4 rounded-xl font-bold text-white text-lg shadow-xl flex items-center justify-center gap-2"
                        style={{ backgroundColor: COLORS.AUTUMN }}
                      >
                        Add to Store
                        <Sparkles className="w-5 h-5" />
                      </motion.button>

                      {/* Success Popup */}
                      <motion.div
                        initial={{ scale: 0, opacity: 0, y: 50 }}
                        whileInView={{ scale: 1, opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 2.3, type: "spring" }}
                        className="absolute bottom-4 right-4 bg-green-500 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2"
                      >
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-semibold text-sm">Design Published!</span>
                      </motion.div>
                    </motion.div>

                    {/* Floating Coffee Cup */}
                    <motion.div
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.4 }}
                      className="absolute -bottom-8 -right-12 hidden lg:block"
                    >
                      <motion.div
                        animate={{ rotate: [0, -10, 10, -10, 0] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        className="text-7xl"
                      >
                        ☕
                      </motion.div>
                    </motion.div>
                  </div>
                </div>

                <div className="order-1">
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-16 h-16 rounded-2xl bg-purple-50 border-2 border-purple-200 flex items-center justify-center">
                        <ImageIconLucide className="w-8 h-8 text-purple-600" />
                      </div>
                      <div className="h-1 w-20 rounded-full" style={{ backgroundColor: COLORS.AUTUMN }}></div>
                    </div>
                    <h3 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                      Upload Your Designs
                    </h3>
                    <p className="text-xl text-gray-600 leading-relaxed mb-8">
                      Simple as posting on Instagram! Upload photos, add your price and description.
                      Your design goes live on your website <span className="font-bold" style={{ color: COLORS.AUTUMN }}>instantly</span>.
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-gray-700">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                        <span className="text-lg">Drag and drop photos</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-700">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                        <span className="text-lg">Add price and details</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-700">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                        <span className="text-lg">Live on your site instantly</span>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Step 3: Website Goes Live */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div className="order-2 lg:order-1">
                  <div className="relative">
                    {/* Floating Step Badge */}
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      whileInView={{ scale: 1, rotate: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.2, type: "spring" }}
                      className="absolute -top-6 -left-6 w-20 h-20 rounded-full flex items-center justify-center text-3xl font-black text-white shadow-2xl z-10"
                      style={{ backgroundColor: COLORS.AUTUMN }}
                    >
                      3
                    </motion.div>

                    {/* Live Website Preview */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9, rotateY: -15 }}
                      whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.3 }}
                      className="bg-white border-4 border-gray-200 rounded-3xl shadow-2xl p-6 relative overflow-hidden"
                    >
                      {/* Browser Chrome */}
                      <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-gray-200">
                        <div className="flex gap-1.5">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        </div>
                        <div className="flex-1 bg-green-50 border-2 border-green-300 rounded-lg px-3 py-1.5 text-xs font-bold flex items-center gap-2" style={{ color: COLORS.AUTUMN }}>
                          <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 1, repeat: Infinity }}
                            className="w-2 h-2 rounded-full bg-green-500"
                          />
                          fashion_designer_priya.vasstr.com 🎉
                        </div>
                      </div>

                      {/* Website Hero */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.6 }}
                        className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-6 mb-4 text-center border-2 border-gray-200"
                      >
                        <motion.h4
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.8 }}
                          className="text-2xl font-bold text-gray-900 mb-2"
                        >
                          Priya's Fashion Studio
                        </motion.h4>
                        <motion.p
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 1 }}
                          className="text-sm text-gray-600"
                        >
                          Handcrafted Designer Wear
                        </motion.p>
                      </motion.div>

                      {/* Product Grid */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        {[0, 1, 2, 3].map((index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0, rotate: -90 }}
                            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                            viewport={{ once: true }}
                            transition={{
                              duration: 0.6,
                              delay: 1.2 + index * 0.1,
                              type: "spring",
                              stiffness: 150
                            }}
                            whileHover={{ scale: 1.05, y: -5 }}
                            className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl p-4 border-2 border-gray-200 cursor-pointer"
                          >
                            <div className="aspect-square bg-white rounded-lg mb-2 flex items-center justify-center text-3xl">
                              {['👗', '👚', '👔', '🧥'][index]}
                            </div>
                            <div className="text-xs font-bold text-gray-900">Design {index + 1}</div>
                            <div className="text-xs font-bold" style={{ color: COLORS.AUTUMN }}>₹2,999</div>
                          </motion.div>
                        ))}
                      </div>

                      {/* CTA Button */}
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 1.8, type: "spring" }}
                        className="bg-white border-2 border-gray-200 rounded-xl p-3 text-center"
                      >
                        <div className="px-4 py-2 rounded-lg font-bold text-white text-sm" style={{ backgroundColor: COLORS.AUTUMN }}>
                          Shop Now
                        </div>
                      </motion.div>

                      {/* Celebration Emojis */}
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 2, type: "spring" }}
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                      >
                        {[...Array(12)].map((_, i) => (
                          <motion.div
                            key={i}
                            initial={{ scale: 0, x: 0, y: 0, opacity: 0 }}
                            whileInView={{
                              scale: [0, 1.5, 0],
                              x: [0, Math.cos(i * 30 * Math.PI / 180) * 150],
                              y: [0, Math.sin(i * 30 * Math.PI / 180) * 150],
                              opacity: [0, 1, 0],
                              rotate: [0, 360]
                            }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.5, delay: 2.1 + i * 0.05 }}
                            className="absolute text-3xl"
                          >
                            {['🎉', '✨', '🎊', '💫'][i % 4]}
                          </motion.div>
                        ))}
                      </motion.div>
                    </motion.div>

                    {/* Floating Laptop with Person */}
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.4 }}
                      className="absolute -bottom-12 -left-16 hidden lg:block"
                    >
                      <div className="relative">
                        <motion.div
                          animate={{ rotate: [0, 5, -5, 0] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                          className="text-8xl"
                        >
                          💻
                        </motion.div>
                        <motion.div
                          initial={{ scale: 0 }}
                          whileInView={{ scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 2.5, type: "spring" }}
                          className="absolute -top-4 -right-8 text-6xl"
                        >
                          <motion.div
                            animate={{ y: [0, -15, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                          >
                            🙌
                          </motion.div>
                        </motion.div>
                      </div>
                    </motion.div>

                    {/* Floating Party Popper */}
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      whileInView={{ scale: 1, rotate: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 2.2, type: "spring" }}
                      className="absolute -top-8 -right-8 text-6xl"
                    >
                      <motion.div
                        animate={{ rotate: [0, -20, 20, 0] }}
                        transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                      >
                        🎊
                      </motion.div>
                    </motion.div>
                  </div>
                </div>

                <div className="order-1 lg:order-2">
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-16 h-16 rounded-2xl bg-green-50 border-2 border-green-200 flex items-center justify-center">
                        <Rocket className="w-8 h-8 text-green-600" />
                      </div>
                      <div className="h-1 w-20 rounded-full" style={{ backgroundColor: COLORS.AUTUMN }}></div>
                    </div>
                    <h3 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                      Your Website is Live!
                    </h3>
                    <p className="text-xl text-gray-600 leading-relaxed mb-8">
                      That's it! Your professional fashion website is now live and ready to receive orders.
                      Share the link, start getting customers, and <span className="font-bold" style={{ color: COLORS.AUTUMN }}>watch your business grow</span>.
                    </p>
                    <div className="space-y-3 mb-8">
                      <div className="flex items-center gap-3 text-gray-700">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                        <span className="text-lg">Website live in 5 minutes</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-700">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                        <span className="text-lg">Professional brand presence</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-700">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                        <span className="text-lg">Start receiving orders immediately</span>
                      </div>
                    </div>
                    <Link to="/Register">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.98 }}
                        className="px-10 py-5 rounded-xl font-bold text-white text-lg shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center gap-3"
                        style={{ backgroundColor: COLORS.AUTUMN }}
                      >
                        <Sparkles className="w-6 h-6" />
                        Create Your Website Now
                        <ArrowRight className="w-6 h-6" />
                      </motion.button>
                    </Link>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </FadeInWhenVisible>

      {/* 6️⃣ HOW IT WORKS */}
      <FadeInWhenVisible>
        <section className="py-20 sm:py-32 px-4 sm:px-6 lg:px-8 bg-white border-t-2 border-gray-200">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="inline-block px-4 py-2 bg-white border-2 border-gray-200 rounded-full text-sm font-semibold mb-6"
                style={{ color: COLORS.AUTUMN }}
              >
                How It Works
              </motion.div>
              <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                Launch Your Website
                <br />
                <span className="text-gray-600">in 3 Simple Steps</span>
              </h2>
              <p className="text-xl text-gray-600">No technical knowledge needed. Seriously.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 sm:gap-12 max-w-5xl mx-auto">
              {[
                {
                  step: "1",
                  icon: UserPlus,
                  title: "Sign Up Free",
                  description: "Create your account and claim your branded URL (yourname.vasstr.com) in 30 seconds"
                },
                {
                  step: "2",
                  icon: Upload,
                  title: "Add Your Designs",
                  description: "Upload photos, prices, and descriptions of your products. As simple as posting on Instagram"
                },
                {
                  step: "3",
                  icon: Rocket,
                  title: "Start Selling",
                  description: "Share your website link and watch orders come in automatically. That's it!"
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="relative text-center"
                >
                  <div className="text-8xl font-bold text-gray-100 mb-4">{item.step}</div>
                  <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6 border-2 border-gray-200 bg-white -mt-16 relative z-10">
                    <item.icon className="w-8 h-8" style={{ color: COLORS.AUTUMN }} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-lg">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </FadeInWhenVisible>

      {/* 5️⃣ TEMPLATES PREVIEW - LANDSCAPE */}
      <FadeInWhenVisible>
        <section className="py-20 sm:py-32 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                Your Brand Could
                <br />
                <span className="text-gray-600">Look Like This</span>
              </h2>
              <p className="text-xl text-gray-600">Choose from 20+ professionally designed templates made for fashion brands</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {[
                {
                  name: "Minimalist White",
                  category: "Clean & Modern",
                  image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=500&fit=crop&auto=format",
                  gradient: "from-gray-50 to-white"
                },
                {
                  name: "Luxury Black",
                  category: "Premium & Elegant",
                  image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=500&fit=crop&auto=format",
                  gradient: "from-gray-900 to-gray-700"
                },
                {
                  name: "Boutique Warm",
                  category: "Artistic & Unique",
                  image: "https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=800&h=500&fit=crop&auto=format",
                  gradient: "from-orange-50 to-pink-50"
                }
              ].map((template, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                  className="group bg-white rounded-2xl overflow-hidden border-2 border-gray-200 hover:border-gray-300 transition-all duration-300 shadow-lg hover:shadow-2xl"
                >
                  {/* Landscape aspect ratio */}
                  <div className="aspect-[16/10] overflow-hidden relative">
                    <img
                      src={template.image}
                      alt={template.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-br ${template.gradient} opacity-20 group-hover:opacity-10 transition-opacity`}></div>
                  </div>
                  <div className="p-6">
                    <div className="text-sm font-semibold mb-1" style={{ color: COLORS.AUTUMN }}>{template.category}</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{template.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Eye className="w-4 h-4" />
                      <span>Preview template</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="text-center">
              <Link to="/Register">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-10 py-5 rounded-xl font-bold text-white text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
                  style={{ backgroundColor: COLORS.AUTUMN }}
                >
                  Browse All 20+ Templates
                </motion.button>
              </Link>
            </div>
          </div>
        </section>
      </FadeInWhenVisible>

      {/* 8️⃣ FEATURES SECTION */}
      <FadeInWhenVisible>
        <section className="py-20 sm:py-32 px-4 sm:px-6 lg:px-8 bg-white border-t-2 border-gray-200">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                Everything You Need
                <br />
                <span className="text-gray-600">Built Right In</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Globe, title: "Your Own Domain", desc: "yourname.vasstr.com - professional branded URL" },
                { icon: Smartphone, title: "Mobile Optimized", desc: "Perfect on phones, tablets, and computers" },
                { icon: CreditCard, title: "Secure Payments", desc: "Razorpay integration for safe transactions" },
                { icon: Package, title: "Product Management", desc: "Easy add, edit, delete with photo uploads" },
                { icon: ShoppingBag, title: "Order Tracking", desc: "See all orders in your dashboard" },
                { icon: Users, title: "Customer Management", desc: "Track all your customers in one place" }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  whileHover={{ y: -5 }}
                  className="bg-white border-2 border-gray-200 hover:border-gray-300 rounded-2xl p-8 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-white border-2 border-gray-200 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-gray-900" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </FadeInWhenVisible>

      {/* TESTIMONIALS */}
      <FadeInWhenVisible>
        <section className="py-20 sm:py-32 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                What Designers Say
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  name: "Priya Sharma",
                  role: "Fashion Designer, Mumbai",
                  text: "I was drowning in Instagram DMs. Now customers just order from my website and I manage everything in minutes. My sales doubled in the first month!",
                  avatar: "PS"
                },
                {
                  name: "Rahul Mehta",
                  role: "Boutique Owner, Delhi",
                  text: "Best decision I made for my business. Customers actually trust me more now that I have a real website. The payment integration is seamless!",
                  avatar: "RM"
                },
                {
                  name: "Ananya Singh",
                  role: "Designer, Bangalore",
                  text: "I can't believe how easy it was to set up. I'm not tech-savvy at all, but I had my website running in 10 minutes. Game changer!",
                  avatar: "AS"
                }
              ].map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white border-2 border-gray-200 hover:border-gray-300 rounded-2xl p-8 transition-all duration-300"
                >
                  <div className="flex gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 text-lg mb-6 leading-relaxed">"{testimonial.text}"</p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: COLORS.AUTUMN }}>
                      {testimonial.avatar}
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.role}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </FadeInWhenVisible>

      {/* FINAL CTA */}
      <FadeInWhenVisible>
        <section className="py-20 sm:py-32 px-4 sm:px-6 lg:px-8 bg-white border-t-2 border-gray-200">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
              Ready to Stop Losing Money
              <br />
              <span className="text-gray-600">on Instagram?</span>
            </h2>
            <p className="text-xl text-gray-600 mb-10">
              Join 500+ fashion designers who already have their professional website
            </p>
            <Link to="/Register">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-12 py-6 rounded-xl font-bold text-white text-xl shadow-2xl hover:shadow-3xl transition-all duration-300"
                style={{ backgroundColor: COLORS.AUTUMN }}
              >
                <span className="flex items-center gap-3">
                  Get Your Free Website Now
                  <ArrowRight className="w-6 h-6" />
                </span>
              </motion.button>
            </Link>
            <p className="text-gray-500 mt-6">
              100% Free to Start • No Credit Card • Live in 5 Minutes
            </p>
          </div>
        </section>
      </FadeInWhenVisible>

      <VFooter />

      {/* VIDEO MODAL */}
      <AnimatePresence>
        {showVideoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            onClick={() => setShowVideoModal(false)}
          >
            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.7, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative w-full max-w-4xl mx-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative w-full aspect-video max-h-[90vh] md:max-h-[80vh]">
                <iframe
                  src="https://www.youtube-nocookie.com/embed/mw9BpIIRJdI?autoplay=1&rel=0&modestbranding=1&playsinline=1"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  className="absolute inset-0 w-full h-full rounded-lg shadow-2xl"
                  title="Fashion Website Builder Demo"
                />
              </div>
              <button
                onClick={() => setShowVideoModal(false)}
                className="absolute -top-3 -right-3 md:-top-4 md:-right-4 bg-white rounded-full p-2 shadow-xl hover:shadow-2xl transition-shadow z-10"
              >
                <X className="w-5 h-5 text-gray-800" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;